import { h } from '../dom.js';
import type { Course, Sentence, Series, Topic } from '../types.js';
import { navigate } from '../router.js';
import { AudioPlayer } from '../audio.js';
import { loadEnhancedMeta, findIndex, prevIndex, nextIndex } from '../metadata.js';
import { createSentenceCard } from './SentenceCard.js';
import { createSentenceList } from './SentenceList.js';
import { createControlBar } from './ControlBar.js';
import { flatCourses } from '../manifest.js';

export interface CoursePlayerOptions {
  series: Series;
  course: Course;
  initialTime?: number;
  initialSentence?: number;
  onTimeChange: (t: number, i: number) => void;
}

export interface CoursePlayerHandle {
  root: HTMLElement;
  destroy: () => void;
  prevSentence: () => void;
  nextSentence: () => void;
  toggle: () => void;
  skip: (delta: number) => void;
  hasMeta: () => boolean;
  jumpToSentenceByIndex: (idx: number) => void;
  prevCourse: () => void;
  nextCourse: () => void;
}

export function createCoursePlayer(opts: CoursePlayerOptions): CoursePlayerHandle {
  const { series, course } = opts;
  const audio = new AudioPlayer();
  const audioUrl = `${series.basePath}/${course.mp3}`;
  audio.load(audioUrl);

  const sentenceCard = createSentenceCard();
  const sentenceList = createSentenceList();
  let sentences: Sentence[] = [];
  let hasMeta = false;
  let currentIdx = -1;
  let pauseAt: number | null = null; // when set, pause once currentTime crosses it
  let manualJumpFrom: 'button' | null = null;

  const listSection = h('section', { className: 'sl-section' }, [
    
    sentenceList.root,
  ]);

  const courseList = flatCourses(series);
  const courseIdx = courseList.findIndex((c) => c.id === course.id);

  const backBtn = h('button', {
    className: 'icon-btn',
    attrs: { type: 'button', 'aria-label': '返回课程列表' },
    on: { click: () => navigate({ name: 'series', seriesId: series.id }) },
    text: '←',
  });
  const topicTitle = h('span', { className: 'player-header__topic player-header__topic--hidden' });

  const header = h('header', { className: 'view-header view-header--tight player-header' }, [
    backBtn,
    h('div', { className: 'view-header__body' }, [
      h('div', { className: 'player-header__line' }, [
        h('h1', { className: 'view-title', text: course.title }),
        topicTitle,
      ]),
      h('p', { className: 'view-subtitle', text: `${series.title} · ${courseIdx + 1} / ${courseList.length}` }),
    ]),
  ]);

  // The manifest carries the topic so it can be shown before the subtitle
  // request completes. The metadata remains the source of truth for older
  // manifests that do not contain course.topic.
  updateTopicTitle(course.topic ?? null);

  const ctl = createControlBar(audio, {
    onToggle: () => { pauseAt = null; audio.toggle(); },
    onPrevSentence: () => prevSentenceAction(),
    onNextSentence: () => nextSentenceAction(),
    onPrevCourse: () => { pauseAt = null; prevCourseAction(); },
    onNextCourse: () => { pauseAt = null; nextCourseAction(); },
    onSeekPercent: (pct) => { pauseAt = null; audio.seek(pct * audio.duration); },
  });

  const root = h('section', { className: 'player-view' }, [
    header,
    ctl.root,
    sentenceCard.root,
    listSection,
    audio.element,
  ]);
  // Tighter control bar: remove extra padding/margin via class
  ctl.root.classList.add('ctl--tight');

  audio.element.style.display = 'none';

  const offTime = audio.onTime(() => {
    if (pauseAt != null && audio.currentTime >= pauseAt - 0.02) {
      const stopAt = pauseAt;
      pauseAt = null;
      audio.pause();
      audio.seek(stopAt - 0.01);
    }
    ctl.update();
    updateSentence();
    opts.onTimeChange(audio.currentTime, currentIdx);
  });
  const offState = audio.onState(() => ctl.update());
  const offEnd = audio.onEnd(() => nextCourseAction());

  if (course.hasMeta && course.meta) {
    const metaUrl = `${series.basePath}/${course.meta}`;
    loadEnhancedMeta(metaUrl)
      .then((meta) => {
        sentences = meta.sentences;
        hasMeta = sentences.length > 0;
        updateTopicTitle(meta.topic ?? course.topic ?? null);
        if (!hasMeta) {
          sentenceCard.setEmpty('元数据为空，仅播放音频');
          listSection.style.display = 'none';
        } else {
          sentenceList.setSentences(sentences, (i) => jumpToSentenceByIndex(i));
        }
        if (opts.initialSentence != null && opts.initialSentence >= 0 && hasMeta) {
          jumpToSentenceByIndex(opts.initialSentence);
        }
        updateSentence();
      })
      .catch((e) => {
        sentenceCard.setEmpty(`加载字幕失败: ${e?.message ?? e}`);
        listSection.style.display = 'none';
      });
  } else {
    sentenceCard.setEmpty('本课暂无字幕/翻译数据。');
    listSection.style.display = 'none';
  }

  function updateTopicTitle(topic: Topic | null): void {
    if (topic && topic.title) {
      topicTitle.textContent = topic.title;
      topicTitle.classList.remove('player-header__topic--hidden');
    } else {
      topicTitle.textContent = '';
      topicTitle.classList.add('player-header__topic--hidden');
    }
  }

  if (opts.initialTime && opts.initialTime > 0) {
    const seekOnce = () => {
      audio.seek(opts.initialTime!);
      audio.element.removeEventListener('loadedmetadata', seekOnce);
    };
    audio.element.addEventListener('loadedmetadata', seekOnce);
  }

  function updateSentence(): void {
    if (!hasMeta) return;
    const t = audio.currentTime;
    const i = findIndex(sentences, t);
    currentIdx = i;
    if (i === -1) {
      // A subtitle is visible only inside its exact [start, end) interval.
      // In particular, never preview the next sentence during an audio gap.
      sentenceCard.setSentence(null, -1, sentences.length);
      sentenceList.setActive(-1);
      return;
    }
    const s = sentences[i]!;
    sentenceCard.setSentence(s, i, sentences.length);
    sentenceList.setActive(i);
  }

  function prevSentenceAction(): void {
    if (!hasMeta) { audio.skip(-5); return; }
    const i = prevIndex(sentences, audio.currentTime);
    if (i === -1) return;
    playSentenceOnce(i);
  }
  function nextSentenceAction(): void {
    if (!hasMeta) { audio.skip(5); return; }
    const i = nextIndex(sentences, audio.currentTime);
    if (i === -1) return;
    playSentenceOnce(i);
  }
  function jumpToSentenceByIndex(i: number): void {
    if (!sentences[i]) return;
    playSentenceOnce(i);
  }
  function playSentenceOnce(i: number): void {
    if (!sentences[i]) return;
    const s = sentences[i]!;
    pauseAt = s.end;
    audio.seek(s.start + 0.001);
    if (!audio.playing) audio.play();
  }
  function prevCourseAction(): void {
    if (courseIdx <= 0) return;
    const prev = courseList[courseIdx - 1]!;
    navigate({ name: 'course', seriesId: series.id, courseId: prev.id });
  }
  function nextCourseAction(): void {
    if (courseIdx >= courseList.length - 1) return;
    const nx = courseList[courseIdx + 1]!;
    navigate({ name: 'course', seriesId: series.id, courseId: nx.id });
  }

  return {
    root,
    destroy: () => {
      offTime();
      offState();
      offEnd();
      audio.pause();
      audio.element.removeAttribute('src');
      audio.element.load();
    },
    prevSentence: prevSentenceAction,
    nextSentence: nextSentenceAction,
    toggle: () => audio.toggle(),
    skip: (d) => audio.skip(d),
    hasMeta: () => hasMeta,
    jumpToSentenceByIndex,
    prevCourse: prevCourseAction,
    nextCourse: nextCourseAction,
  };
}
